import { Skeleton } from '@mui/material'
import Pill from 'ext-ui/components/Pill/Pill'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import LockedIcon from 'ext-ui/svg/icon/LockedIcon'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { DropdownValue } from 'src/components/CreditCardDropdownV2/CreditCardDropdownV2'
import TransactionExpenseDetailsFormV2 from 'src/components/TransactionExpenseDetailsFormV2/TransactionExpenseDetailsFormV2'
import { useTranslate } from 'src/localization/Trns'
import {
  ConnectedPlatformStatus,
  TransactionDetailModel,
} from 'src/models/transaction.models'
import { useQueryActiveTransactionIntegration } from 'src/queries/integrations/orgIntegrations.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

import style, {
  skeletonCategory,
  skeletonLabel,
  skeletonTitle,
} from './ExpenseDetailsSection.style'

interface ExpenseDetailsSectionProps {
  transaction: TransactionDetailModel
}

const ExpenseDetailsSection: React.FC<ExpenseDetailsSectionProps> = ({
  transaction,
}): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const { data: activeTransactionIntegration } =
    useQueryActiveTransactionIntegration(transaction.id)
  const { data: expCatSchema, isPending: expCatSchemaLoading } =
    useQueryExpenseCategories({
      filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
    })

  const onSuccessfulSubmit = (
    categoryId: string,
    selectedValue: DropdownValue | undefined,
  ): void => {
    analytics?.track(
      'transaction detail - edit transaction vendor and expense categories - button press',
      {
        intent: 'edit transaction vendor and expense categories',
        interaction: 'button press',
        screen: calculatedBackgroundLocation.pathname,
        expenseDetails: [
          {
            categoryId,
            labelId: selectedValue?.value ?? '',
          },
        ],
      },
    )
  }

  if (expCatSchemaLoading) {
    return (
      <div className={'flex-column gap-5'}>
        <Skeleton variant={'rounded'} css={skeletonTitle} />
        {[1, 2, 3].map((_, idx) => (
          <div key={idx} className={'flex-column gap-2'}>
            <Skeleton variant={'rounded'} css={skeletonCategory} />
            <Skeleton variant={'rounded'} css={skeletonLabel} />
          </div>
        ))}
      </div>
    )
  }

  if (expCatSchema === undefined || expCatSchema.length === 0) {
    return <></>
  }

  const isMissingRequiredExpenseCategories = useMemo(() => {
    const requiredExtCats = expCatSchema.filter(
      (cat) =>
        cat.requiredForTransactions &&
        ![ExpenseCategoryType.Vendor, ExpenseCategoryType.Customer].includes(
          cat.type,
        ),
    )

    // Customer and Vendor need special treatment due to not having categoryIds
    const customer = expCatSchema.find(
      (cat) => cat.type === ExpenseCategoryType.Customer,
    )
    const customerRequired = customer?.requiredForTransactions ?? false
    const customerMissing =
      customerRequired &&
      transaction?.expenseDetails?.find(
        (expDet) => expDet.categoryType === ExpenseCategoryType.Customer,
      ) === undefined
    const vendor = expCatSchema.find(
      (cat) => cat.type === ExpenseCategoryType.Vendor,
    )
    const vendorRequired = vendor?.requiredForTransactions ?? false
    const vendorMissing =
      vendorRequired &&
      transaction?.expenseDetails?.find(
        (expDet) => expDet.categoryType === ExpenseCategoryType.Vendor,
      ) === undefined

    return (
      customerMissing ||
      vendorMissing ||
      requiredExtCats.some((reqCat) => {
        const expDetail = transaction?.expenseDetails?.find(
          (expDet) => expDet.categoryId === reqCat.id,
        )
        // Checking labelName to accomodate for free text
        return expDetail === undefined || expDetail.labelName === undefined
      })
    )
  }, [expCatSchema, transaction?.expenseDetails])

  return (
    <div data-testid={'expense-categories-box'} css={style}>
      <div className={'header'}>
        {activeTransactionIntegration?.syncStatus ===
          ConnectedPlatformStatus.Success && (
          <TooltipIcon icon={<LockedIcon />} placement={'top'}>
            <div className={'flex-column gap-4'}>
              <p>
                {translate(
                  'Expense Categories for this transaction have been synced to ${orgLvlIntegration} and are uneditable.',
                  {
                    orgLvlIntegration: activeIntegration?.platformName || '',
                  },
                )}
              </p>
              <p>
                {translate(
                  'Once synced, these fields can only be updated in ${orgLvlIntegration}.',
                  {
                    orgLvlIntegration: activeIntegration?.platformName || '',
                  },
                )}
              </p>
            </div>
          </TooltipIcon>
        )}
        <span className={'font-h3'}>{translate('Expense Categories')}</span>
        {isMissingRequiredExpenseCategories && (
          <Pill size={'small'} color={'severe'}>
            {translate('Missing')}
          </Pill>
        )}
      </div>

      <TransactionExpenseDetailsFormV2
        expCatSchema={expCatSchema}
        transactionId={transaction?.id}
        expenseDetails={transaction?.expenseDetails}
        onExpenseCategorySelectSuccess={onSuccessfulSubmit}
      />
    </div>
  )
}

export default ExpenseDetailsSection
